/**
 * Programmatischer, deterministischer Fiktiv-Chart auf SVG-Basis.
 * Ausgang: chart_seed (Hex-String) macht das Muster stabil pro Strategie.
 * Zeichnet Kerzen, Trend, Entry-Zone, SL- und TP-Linien.
 */
const WIDTH = 640;
const HEIGHT = 260;
const PAD_L = 44;
const PAD_R = 120;
const PAD_T = 20;
const PAD_B = 32;
const CANDLES = 40;

function seededRandom(seed) {
  let s = 0;
  for (let i = 0; i < seed.length; i++) s = (s * 31 + seed.charCodeAt(i)) >>> 0;
  return () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 0xffffffff;
  };
}

function generateSeries(seed, tradeTypes) {
  const rnd = seededRandom(seed || "default");
  const isSwingOrHodl =
    tradeTypes?.includes("swingtrade") || tradeTypes?.includes("hodl");
  const drift = isSwingOrHodl ? 0.4 : 0.15;
  const volatility = tradeTypes?.includes("scalp") ? 0.9 : 1.4;
  let price = 100;
  const candles = [];
  for (let i = 0; i < CANDLES; i++) {
    const open = price;
    const change = (rnd() - 0.5) * volatility * 2 + drift * (rnd() - 0.3);
    const close = Math.max(20, open + change);
    const high = Math.max(open, close) + rnd() * volatility * 0.9;
    const low = Math.min(open, close) - rnd() * volatility * 0.9;
    candles.push({ open, close, high, low });
    price = close;
  }
  return candles;
}

export default function FictionalChart({
  seed = "default",
  tradeTypes = [],
  hasSL = false,
  hasTP = false,
  hasScreenshot = false,
}) {
  const candles = generateSeries(seed, tradeTypes);
  const highs = candles.map((c) => c.high);
  const lows = candles.map((c) => c.low);
  const maxP = Math.max(...highs);
  const minP = Math.min(...lows);
  const range = maxP - minP;
  const chartH = HEIGHT - PAD_T - PAD_B;
  const chartW = WIDTH - PAD_L - PAD_R;
  const cw = chartW / CANDLES;

  const y = (p) => PAD_T + ((maxP - p) / range) * chartH;

  const entryPrice = candles[CANDLES - 8].close;
  const slPrice = hasSL ? entryPrice - range * 0.15 : null;
  const tp1Price = hasTP ? entryPrice + range * 0.22 : null;
  const tp2Price = hasTP ? entryPrice + range * 0.42 : null;
  const entryX = PAD_L + (CANDLES - 8) * cw + cw / 2;

  return (
    <div className="rounded-xl border border-border bg-card p-3" data-testid="fictional-chart">
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="overline">Fiktiver Chart · zur Veranschaulichung</div>
        {hasScreenshot && (
          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-primary/15 border border-primary/30 text-primary-foreground">
            Screenshot berücksichtigt
          </span>
        )}
      </div>
      <svg
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        className="w-full h-auto"
        role="img"
        aria-label="Fiktiver Beispielchart mit eingezeichnetem Setup"
      >
        {/* Achsen-Rahmen */}
        <rect
          x={PAD_L}
          y={PAD_T}
          width={chartW}
          height={chartH}
          fill="none"
          stroke="hsl(var(--border))"
          strokeOpacity="0.5"
        />

        {/* Grid */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            x1={PAD_L}
            y1={PAD_T + chartH * f}
            x2={PAD_L + chartW}
            y2={PAD_T + chartH * f}
            stroke="hsl(var(--border))"
            strokeOpacity="0.25"
            strokeDasharray="2 4"
          />
        ))}

        {/* Kerzen */}
        {candles.map((c, i) => {
          const up = c.close >= c.open;
          const x = PAD_L + i * cw + cw / 2;
          const bodyTop = y(Math.max(c.open, c.close));
          const bodyBot = y(Math.min(c.open, c.close));
          const bodyH = Math.max(1, bodyBot - bodyTop);
          const color = up ? "hsl(122 42% 55%)" : "hsl(0 66% 66%)";
          return (
            <g key={i}>
              <line
                x1={x}
                y1={y(c.high)}
                x2={x}
                y2={y(c.low)}
                stroke={color}
                strokeWidth="1"
                strokeOpacity="0.8"
              />
              <rect
                x={x - cw * 0.35}
                y={bodyTop}
                width={cw * 0.7}
                height={bodyH}
                fill={color}
                fillOpacity="0.85"
              />
            </g>
          );
        })}

        {/* Entry-Marker */}
        <g>
          <line
            x1={entryX - cw * 1.2}
            y1={y(entryPrice)}
            x2={PAD_L + chartW}
            y2={y(entryPrice)}
            stroke="hsl(var(--primary))"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <text
            x={PAD_L + chartW + 6}
            y={y(entryPrice) + 4}
            fontSize="11"
            fontFamily="ui-monospace"
            fill="hsl(var(--primary))"
          >
            Entry {entryPrice.toFixed(2)}
          </text>
        </g>

        {/* SL */}
        {slPrice !== null && (
          <g>
            <line
              x1={PAD_L}
              y1={y(slPrice)}
              x2={PAD_L + chartW}
              y2={y(slPrice)}
              stroke="hsl(0 66% 66%)"
              strokeWidth="1.5"
              strokeDasharray="3 4"
            />
            <text
              x={PAD_L + chartW + 6}
              y={y(slPrice) + 4}
              fontSize="11"
              fontFamily="ui-monospace"
              fill="hsl(0 66% 78%)"
            >
              SL {slPrice.toFixed(2)}
            </text>
          </g>
        )}

        {/* TPs */}
        {tp1Price !== null && (
          <g>
            <line
              x1={PAD_L}
              y1={y(tp1Price)}
              x2={PAD_L + chartW}
              y2={y(tp1Price)}
              stroke="hsl(122 42% 55%)"
              strokeWidth="1.5"
              strokeDasharray="3 4"
            />
            <text
              x={PAD_L + chartW + 6}
              y={y(tp1Price) + 4}
              fontSize="11"
              fontFamily="ui-monospace"
              fill="hsl(122 42% 72%)"
            >
              TP1 {tp1Price.toFixed(2)}
            </text>
          </g>
        )}
        {tp2Price !== null && (
          <g>
            <line
              x1={PAD_L}
              y1={y(tp2Price)}
              x2={PAD_L + chartW}
              y2={y(tp2Price)}
              stroke="hsl(122 42% 55%)"
              strokeWidth="1"
              strokeDasharray="2 4"
              strokeOpacity="0.7"
            />
            <text
              x={PAD_L + chartW + 6}
              y={y(tp2Price) + 4}
              fontSize="11"
              fontFamily="ui-monospace"
              fill="hsl(122 42% 72%)"
              fillOpacity="0.9"
            >
              TP2 {tp2Price.toFixed(2)}
            </text>
          </g>
        )}
      </svg>
    </div>
  );
}
